// @workflow assistant.voice
/**
 * Read aloud as a pure state machine, for the whole chat: one reply at a time is loading or
 * playing. `now` (phase, which reply, which session, how the last one ended) and `progress`
 * (whole seconds played, the total once known) are separate objects, so a progress tick leaves
 * `now` as it was and the reply buttons don't re-render every second.
 *
 * Every event carries the session it belongs to; an event from an older session (a stopped or
 * replaced reading, even of the same reply) changes nothing.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { MESSAGE_STATUS, ROLE } from './events.js';

export const READ_PHASE = Object.freeze({ IDLE: 'idle', LOADING: 'loading', PLAYING: 'playing' });
export const READ_OUTCOME = Object.freeze({ FINISHED: 'finished', STOPPED: 'stopped', FAILED: 'failed' });
export const READ_ACTION = Object.freeze({
  START: 'start',
  PLAYING: 'playing',
  PROGRESS: 'progress',
  ENDED: 'ended',
  STOP: 'stop',
  FAILED: 'failed',
});

const NO_PROGRESS = Object.freeze({ played: 0, duration: null });

/** `last`: how the reading before ended, `{ messageId, outcome, code }`, for what the screen reader hears. */
export const IDLE_READING = Object.freeze({
  now: Object.freeze({ phase: READ_PHASE.IDLE, messageId: null, session: 0, last: null }),
  progress: NO_PROGRESS,
});

/** Replies whose actions show (the same rule as Copy and the ratings). */
const READABLE = Object.freeze([MESSAGE_STATUS.DONE, MESSAGE_STATUS.STOPPED]);

const isActive = (now) => now.phase !== READ_PHASE.IDLE;

/** Loading or playing this reply. */
export const isReading = (now, messageId) => isActive(now) && now.messageId === messageId;

/**
 * Whether a reply shows Read aloud: a finished reply with the server's id (the server reads it
 * from its own store).
 */
export function canReadAloud(message) {
  return !!(message && message.role === ROLE.ASSISTANT && READABLE.includes(message.status) && typeof message.id === 'string' && message.id);
}

const ended = (state, outcome, code = null) => Object.freeze({
  now: Object.freeze({ ...IDLE_READING.now, last: Object.freeze({ messageId: state.now.messageId, outcome, code }) }),
  progress: NO_PROGRESS,
});

const knownDuration = (d) => (typeof d === 'number' && Number.isFinite(d) && d > 0 ? d : null);

function progressed(state, { played, duration }) {
  const next = { played: Math.max(0, Math.floor(Number(played) || 0)), duration: knownDuration(duration) };
  const { progress } = state;
  if (next.played === progress.played && next.duration === progress.duration) return state;
  return Object.freeze({ now: state.now, progress: Object.freeze(next) });
}

/**
 * @param {typeof IDLE_READING} state
 * @param {{ type: string, messageId?: string, session?: number, played?: number, duration?: number|null, code?: string }} action
 */
export function nextReading(state, action) {
  const { now } = state;
  const current = action.session === now.session && isActive(now);
  switch (action.type) {
    case READ_ACTION.START:
      return Object.freeze({ now: Object.freeze({ phase: READ_PHASE.LOADING, messageId: action.messageId, session: action.session, last: null }), progress: NO_PROGRESS });
    case READ_ACTION.PLAYING:
      return current && now.phase === READ_PHASE.LOADING ? Object.freeze({ now: Object.freeze({ ...now, phase: READ_PHASE.PLAYING }), progress: state.progress }) : state;
    case READ_ACTION.PROGRESS:
      return current && now.phase === READ_PHASE.PLAYING ? progressed(state, action) : state;
    case READ_ACTION.ENDED:
      return current ? ended(state, READ_OUTCOME.FINISHED) : state;
    case READ_ACTION.FAILED:
      return current ? ended(state, READ_OUTCOME.FAILED, action.code || null) : state;
    case READ_ACTION.STOP:
      return isActive(now) ? ended(state, READ_OUTCOME.STOPPED) : state;
    default:
      return state;
  }
}
